import { useEffect, useRef } from 'react';
import { create } from 'zustand';

// 页面把现有按钮逻辑登记为命令实现; 卸载时自动注销。
// 用 ref 保存最新闭包 —— 页面状态每次渲染都在变, 执行时必须拿到当下的值。

export type AgentArgs = Record<string, unknown>;
export type AgentHandler = (args: AgentArgs) => unknown | Promise<unknown>;
type HandlerMap = Record<string, AgentHandler>;

type RegistryState = {
  handlers: Record<string, { owner: symbol; run: AgentHandler }>;
  describers: Array<{ owner: symbol; describe: () => string }>;
  register: (owner: symbol, ids: string[], resolve: (id: string) => AgentHandler, describe?: () => string) => void;
  unregister: (owner: symbol) => void;
};

export const useAgentRegistry = create<RegistryState>((set) => ({
  handlers: {},
  describers: [],
  register: (owner, ids, resolve, describe) => set((state) => ({
    handlers: {
      ...state.handlers,
      ...Object.fromEntries(ids.map((id) => [id, { owner, run: (args: AgentArgs) => resolve(id)(args) }])),
    },
    describers: describe ? [...state.describers.filter((d) => d.owner !== owner), { owner, describe }] : state.describers,
  })),
  unregister: (owner) => set((state) => ({
    handlers: Object.fromEntries(Object.entries(state.handlers).filter(([, h]) => h.owner !== owner)),
    describers: state.describers.filter((d) => d.owner !== owner),
  })),
}));

/**
 * 页面登记命令实现:
 *   useAgentCommands({ 'fracsim.run': () => runSim() }, () => `当前方案 ${designId}`);
 * 处理函数返回字符串时作为执行结果展示给用户, 抛错视为失败。
 */
export function useAgentCommands(handlers: HandlerMap, describe?: () => string) {
  const latest = useRef({ handlers, describe });
  useEffect(() => { latest.current = { handlers, describe }; });
  const ids = Object.keys(handlers).sort().join('|');
  const hasDescribe = Boolean(describe);

  useEffect(() => {
    const owner = Symbol('agent-commands');
    useAgentRegistry.getState().register(
      owner,
      ids ? ids.split('|') : [],
      (id) => latest.current.handlers[id],
      hasDescribe ? () => latest.current.describe?.() ?? '' : undefined,
    );
    return () => useAgentRegistry.getState().unregister(owner);
  }, [ids, hasDescribe]);
}

export const getAgentHandler = (id: string) => useAgentRegistry.getState().handlers[id]?.run;

export const describeCurrentPage = () =>
  useAgentRegistry.getState().describers.map((d) => {
    try { return d.describe(); } catch { return ''; }
  }).filter(Boolean).join('\n');

/** 跳转后等页面挂载并登记命令; 超时返回 false。 */
export function waitForAgentHandler(id: string, timeoutMs = 10000): Promise<boolean> {
  if (getAgentHandler(id)) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => { unsubscribe(); resolve(false); }, timeoutMs);
    const unsubscribe = useAgentRegistry.subscribe((state) => {
      if (state.handlers[id]) {
        window.clearTimeout(timer);
        unsubscribe();
        resolve(true);
      }
    });
  });
}
